import * as Icons from "heroicons-react";
import { signInUser } from "../../Utility/Auth";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSupplierWisePurchaseQuery } from "../../../services/reportsApi";
import { useWarehouseQuery } from "../../../services/warehouseApi";
import WareHouseDW from "../../Common/CustomSelect/WareHouseDW";
import SelectSupplierReport from "../../Common/CustomSelect/SelectSupplierReport";

const SupplierWisePurchaseReport = () => {
  const auth = signInUser();

  const [warehouse, setWarehouse] = useState("allWh");
  const [supplier, setSupplierId] = useState();

  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const { aamarId } = auth;
  const lang = useSelector((state) => state.languageReducer);
  const { data } = useSupplierWisePurchaseQuery({
    startDate,
    endDate,
    warehouse,
    aamarId,
    supplier,
  });

  // console.log("User Wise Sales Report Data:", data, supplier);

  // fine warehouse Name
  const [whName, setWhName] = useState("All Warehouses");

  const { data: wh, refetch } = useWarehouseQuery(warehouse);

  // Call refetch only if user?.warehouse changes
  useEffect(() => {
    if (warehouse) {
      refetch();
    }
  }, [warehouse, refetch]);

  // Update warehouse name when data changes
  useEffect(() => {
    if (wh?.name) {
      setWhName(wh.name); // Directly set the name, no need for optional chaining here
    }
  }, [wh]);
  // console.log("Customer Wise Sales Report Data:", data);

  // Export CSV
  const handleExportCSV = () => {
    if (!data || data.length === 0) {
      alert("No data available for export.");
      return;
    }

    const start = new Date(startDate).toLocaleString();
    const end = new Date(endDate).toLocaleString();
    // const end = data[data.length - 1]?.createdAt;
    const generatedOn = new Date().toLocaleString();
    const generatedBy = auth?.name || "Aamar Dokan";

    const storeName =
      auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportTitle = "Supplier Wise Purchase Report";

    const headerInfo = [
      `,Store: ${storeName},,,,,Warehouse: ${warehouseName}`,
      `,Report: ${reportTitle},,,,,Date Range: ${start} to ${end}`,
      `,Generated On: ${generatedOn},,,,Generated By: ${generatedBy}`,
      "",
      "",
    ];

    const csvTableHeader = [
      "S/N",
      "GRN No",
      "Supplier Name",
      "Phone Number",
      "Supplier Company",
      "Total Purchase Invoices",
      "Total Items Purchased",
      "Total Amount",
      "Total Paid",
      "Last Purchase Date",
      "PO No",
      "Date",
      "Total Items",
      "Warehouse",
      "Created By",
    ].join(",");

    let totalItemsPurchased = 0;
    let totalAmount = 0;
    let totalPaid = 0;
    let totalInvoices = 0;

    const csvRows = data?.map((row, index) => {
      const itemsPurchased = Number(row?.totalItemsPurchased || 0);
      const amount = Number(row?.totalAmount || 0);
      const paid = Number(row?.totalPaid || 0);
      const invoices = Number(row?.totalPurchaseInvoices || 0);

      totalItemsPurchased += itemsPurchased;
      totalAmount += amount;
      totalPaid += paid;
      totalInvoices += invoices;

      return [
        index + 1,
        row?.poNo || "",
        row?.supplierName || "",
        row?.phoneNumber || "",
        row?.supplierCompany || "",
        invoices,
        itemsPurchased,
        amount,
        paid,
        row?.lastPurchaseDate || "",
        row?.poNo || "",
        row?.date || "",
        row?.totalItems || "",
        row?.warehouse || "",
        row?.createdBy || "",
      ].join(",");
    });

    const summaryLabelRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "** TOTAL SUMMARY **",
    ].join(",");

    const summaryHeaderRow = [
      "",
      "",
      "",
      "",
      "",
      "Total Purchase Invoices",
      "Total Items Purchased",
      "Total Amount",
      "Total Paid",
    ].join(",");

    const summaryDataRow = [
      "",
      "",
      "",
      "",
      "",
      totalInvoices,
      totalItemsPurchased,
      totalAmount,
      totalPaid,
    ].join(",");

    const csvContent = [
      ...headerInfo,
      csvTableHeader,
      ...csvRows,
      "",
      "",
      summaryLabelRow,
      summaryHeaderRow,
      summaryDataRow,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SupplierWisePurchaseReport.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");

    if (data?.length === 0) {
      alert("No sales data found.");
      return;
    }

    const options = { month: "numeric", day: "numeric", year: "numeric" };
    const formattedStart = new Date(startDate).toLocaleDateString(
      "en-US",
      options
    );
    const formattedEnd = new Date(endDate).toLocaleDateString("en-US", options);
    const reportGeneratedAt = new Date().toLocaleString();

    const storeName =
      auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportName = "Supplier Wise Purchase Report";

    // Title
    doc.setFontSize(16);
    doc.text(storeName, 140, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text(reportName, 140, 22, { align: "center" });

    // Header info - Justify between
    doc.setFontSize(10);
    doc.text(`Store: ${storeName}`, 14, 28);
    doc.text(`Warehouse: ${warehouseName}`, 14, 33);
    doc.text(`Date: ${formattedStart} - ${formattedEnd}`, 14, 39);
    // doc.text(`Report: ${reportName}`, 140, 28, { align: "center" });
    doc.text(`Generated At: ${reportGeneratedAt}`, 280, 28, { align: "right" });
    doc.text(`Generated By: ${auth?.name}`, 280, 33, { align: "right" });

    // Table header
    const headers = [
      [
        "S/N",
        "GRN No",
        "Supplier Name",
        "Phone Number",
        "Supplier Company",
        "Total Purchase Invoices",
        "Total Items Purchased",
        "Total Amount",
        "Total Paid",
        "Last Purchase Date",
        "PO No",
        "Date",
        "Total Items",
        "Warehouse",
        "Created By",
      ],
    ];

    const tableData = data?.map((item, index) => [
      index + 1,
      item?.grnNo,
      item?.supplierName,
      item?.phoneNumber,
      item?.supplierCompany,
      item?.totalPurchaseInvoices,
      item?.totalItemsPurchased,
      parseFloat(item?.totalAmount || 0).toFixed(2),
      parseFloat(item?.totalPaid || 0).toFixed(2),
      item?.lastPurchaseDate ? new Date(item.lastPurchaseDate).toLocaleDateString() : "N/A",
      item?.poNo || "N/A",
      item?.date ? new Date(item.date).toLocaleDateString() : "N/A",
      item?.totalItems || 0,
      item?.warehouse || whName,
      item?.createdBy || "N/A",
    ]);

    autoTable(doc, {
      head: headers,
      body: tableData,
      startY: 45,
      theme: "grid",
      styles: {
        fontSize: 6,
        halign: "center",
      },
      headStyles: {
        fillColor: [242, 101, 45], // tomato background
        textColor: [255, 255, 255], // white text
        fontStyle: "bold",
        lineWidth: 0.2, // line thickness
        lineColor: [255, 255, 255], // white border color
      },
    });

    // === Summary Box ===
    const finalY = doc.lastAutoTable.finalY + 10;
    const lineHeight = 7;
    const boxHeight = 7 * lineHeight + 10;
    const boxWidth = 100;

    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - boxWidth - 15;
    const boxY = finalY;

    // Draw dotted border
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
    doc.setLineDashPattern([], 0); // reset to solid

    let y = boxY + 10;
    const labelX = boxX + 5;
    const valueX = boxX + boxWidth - 5;
    doc.setFontSize(9);

    const totalPurchaseInvoices = sumField(data, "totalPurchaseInvoices");
    const totalItemsPurchased = sumField(data, "totalItemsPurchased");
    const totalAmount = sumField(data, "totalAmount");
    const totalPaid = sumField(data, "totalPaid");
    const totalItems = sumField(data, "totalItems");

    doc.text("Total Purchase Invoices:", labelX, y);
    doc.text(`${totalPurchaseInvoices}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total Items Purchased:", labelX, y);
    doc.text(`${totalItemsPurchased}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total Amount:", labelX, y);
    doc.text(`${totalAmount}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total Paid:", labelX, y);
    doc.text(`${totalPaid}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total Items:", labelX, y);
    doc.text(`${totalItems}`, valueX, y, { align: "right" });

    // Save the PDF
    doc.save("Supplier_Wise_.pdf");
  };

  // Utility to sum values from objects
  const sumField = (data, field) =>
    data
      .reduce((acc, item) => acc + (parseFloat(item[field]) || 0), 0)
      .toFixed(2);

  //warehouse
  const handleOnchangeWareHouseFrom = (e) => {
    if (e.option !== "no-warehouse") {
      setWarehouse(e.option);
    } else {
      setWarehouse("allWh");
    }
  };

  return (
    <div>
      <div>
        <h2 className="mb-3">
          <Icons.ShoppingCartOutline size={35} /> Supplier Wise Purchase Report
        </h2>
      </div>

      <div
        className="mt-3 w-40 p-3"
        style={{ backgroundColor: "#f1f3f5", borderRadius: "8px" }}
      >
        <div className="d-flex mt-2 mb-2 align-items-center justify-content-center gap-2">
          <DatePicker
            selected={new Date(startDate)}
            className="form-control w-100"
            onChange={(date) =>
              setStartDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
          <DatePicker
            selected={new Date(endDate)}
            className="form-control me-4"
            onChange={(date) =>
              setEndDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
        </div>

        <div className="">
          {auth?.type === "admin" && (
            <div className="my-3">
              <WareHouseDW
                id="warehouse"
                name="warehouse"
                handleOnChange={handleOnchangeWareHouseFrom}
                className="form-control"
              />
            </div>
          )}
          <SelectSupplierReport
            aamarId={aamarId}
            onUserSelect={setSupplierId}
          />
        </div>

        <div className="d-flex gap-3 justify-content-center align-items-center text-center">
          {/* Download CSV Button */}
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportCSV}
          >
            <Icons.DownloadOutline size={22} /> Download CSV
          </button>

          {/* Download PDF Button */}
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportPDF}
          >
            <Icons.DocumentOutline size={22} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierWisePurchaseReport;
